// E0.1.1 — Avatar do usuário com fallback chain (img → BoringAvatar SVG → iniciais).
// Substitui placeholders cinza no chat (MessageBubble, ChatPage header).
// Seed determinístico: mesmo userId → mesmo SVG sempre.
import BoringAvatar from "boring-avatars";
import { cn } from "@/lib/utils";
import { useSignedImageUrl } from "@/hooks/useSignedImageUrl";

// Paleta do avatar gerado.
//
// 🔴 A versão anterior tirava as 5 cores direto do `tailwind.config.ts`, e três
// delas eram **quase brancas**: `#b2ebff`, `#89f3f6` e `#edfaff`. Elas existem
// para pintar FUNDO DE PÁGINA, não uma bolinha de 36px sobre fundo branco.
//
// Ao abrir `/mensagens` em 31/08/2026, as duas conversas diretas saíram como
// dois borrões azul-claros iguais. Medindo o SVG renderizado: a AIRIS ficou com
// fundo `#edfaff` e traços `#b2ebff` — ΔE **5,7** da página branca. Um disco em
// branco. E o pedido era justamente sair das "dez linhas idênticas".
//
// ⚠️ As cores abaixo não foram escolhidas no olho — foram medidas. Toda cor
// está a ΔE ≥ 42 da página, e o par mais próximo a ΔE 40,7 (o mínimo exigido é
// 25). O `beam` do BoringAvatar sorteia FUNDO e TRAÇOS da mesma lista, então
// duas cores próximas fazem o rosto sumir dentro do fundo — daí a segunda
// exigência. Ver `__tests__/UserAvatar.contraste.test.tsx`.
//
// As duas primeiras são as da marca (`ep-primary` e `ep-error`) e ficam. As
// outras quatro variam em MATIZ, não em luminosidade: é matiz que separa duas
// pessoas lado a lado numa lista.
export const BRAND_PALETTE = [
  "#13677b", // ep-primary — o teal da marca
  "#9f403d", // ep-error — o contraponto quente da marca
  "#3c3c85", // índigo
  "#3c853c", // verde
  "#bd75ab", // malva
  "#bdab75", // areia
];

export interface UserAvatarProps {
  /** UUID estável do usuário — seed determinístico do BoringAvatar. */
  userId?: string | null;
  /** Nome legível, usado no alt/aria-label e fallback de seed. */
  name?: string | null;
  /** URL da imagem de avatar; se ausente ou onError, cai pro SVG. */
  avatarUrl?: string | null;
  /** Tamanho em px (default 32). Story spec: 32 lista / 40 header. */
  size?: number;
  className?: string;
  /** Força modo iniciais (raro: contextos sem suporte a SVG). */
  useInitials?: boolean;
  /**
   * O nome já está ao lado — não repita para o leitor de tela.
   *
   * 🔴 Sem isto, um item de lista é anunciado como *"Avatar de ACLECIA
   * MARIA DE CARVALHO, ACLECIA MARIA DE CARVALHO"*: o `aria-label` daqui
   * mais o texto do próprio item. É o caso de TODA tela de chat, onde o
   * avatar é ilustração do nome que está do lado.
   *
   * ⚠️ O padrão é `false` porque nem todo lugar mostra o nome — no cartão
   * do organograma o avatar pode ser a única pista de quem é.
   */
  decorativo?: boolean;
}

function computeInitials(name?: string | null): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  // Nome único → 2 primeiras letras ("Augusto" → "AU").
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  // Múltiplos nomes → inicial do 1º + inicial do 2º ("Augusto Silva Oliveira" → "AS").
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export function UserAvatar({
  userId,
  name,
  avatarUrl,
  size = 32,
  className,
  useInitials = false,
  decorativo = false,
}: UserAvatarProps) {
  // Story 1.5 — congela a `<img src>` pelo PATHNAME (ignora a query de
  // assinatura, que muda a cada resposta). Sem isto, todo refetch da lista de
  // colegas (react-query, `staleTime` de 10min mas ainda assim refetch em
  // foco/reconexão) troca a assinatura de CADA avatar, e o browser re-baixa
  // todas as imagens ao mesmo tempo — bug da story 1.5, "todos os avatares
  // piscam". `onError` tenta a assinatura fresca UMA vez antes de desistir
  // pro SVG (AC 4 da story 1.5).
  const { displayUrl, failed, onError, onLoad } = useSignedImageUrl(avatarUrl);

  const displayName = name || "usuário";
  const seed = userId || name || null;

  const showImage = !!displayUrl && !failed && !useInitials;
  const showSvg = !showImage && !useInitials && !!seed;
  const showInitials = !showImage && !showSvg;

  return (
    <span
      aria-label={decorativo ? undefined : `Avatar de ${displayName}`}
      aria-hidden={decorativo ? "true" : undefined}
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted",
        className
      )}
      style={{ width: size, height: size }}
    >
      {showImage && (
        <img
          src={displayUrl ?? undefined}
          alt={decorativo ? "" : `Avatar de ${displayName}`}
          width={size}
          height={size}
          // Passo 27 — a lista de colaboradores desenha as 150 linhas de uma vez:
          // sem `lazy`, abrir a lista baixava TODAS as fotos. O cabeçalho, o Início
          // e o perfil não usam este componente (têm `<img>` próprio, sempre à vista).
          loading="lazy"
          decoding="async"
          onError={onError}
          onLoad={onLoad}
          className="h-full w-full object-cover"
        />
      )}
      {showSvg && (
        <BoringAvatar name={seed as string} variant="beam" colors={BRAND_PALETTE} size={size} />
      )}
      {showInitials && (
        <span
          aria-hidden="true"
          className="font-medium text-foreground"
          style={{ fontSize: Math.max(10, Math.round(size * 0.4)) }}
        >
          {computeInitials(name)}
        </span>
      )}
    </span>
  );
}
